// Typing into a streamed Mac window from the web app (Josh, 09-29: "I tried to stream a window on the web app, the
// stream came up but I couldn't type into boxes, and backspacing is frustrating. It should forward keystrokes
// instead of some input box."). The headset's counterpart is the stream's KeyProxy (MacWindows.swift): hardware
// keys go to the Mac as physical keys, the on-screen keyboard as text. Nothing is typed into a box here.
//
// - A desktop keyboard: every keydown / keyup while a stream has the keyboard goes to the Mac as it happens, as the
//   key's position (`event.code` → HID usage, as WindowInput.swift takes it; the Mac's layout decides the
//   character), with Shift / Control / Option / Command and key repeat. On a PC, Control is the Mac's Command and
//   the Windows key its Control. Left to the browser: reload, tab switching and what it never lets a page stop
//   (Cmd/Ctrl-R, -Tab, -1…9, -W, -T, -N, -Q, developer tools). Dead keys and IMEs compose here in a hidden field
//   and the composed text goes as text when the composition ends. Shift-Esc stops typing to the Mac.
// - A phone or tablet: the stream's bar has a keyboard button that raises the soft keyboard on a hidden field that
//   never shows as a box and is kept empty — each `beforeinput` goes to the Mac at once (text as text, Backspace,
//   Return, …) — and a strip of keys the soft keyboard lacks (Esc, Tab, arrows, Cmd and Ctrl, which latch).
// - "Typing to <window>" shows while keys go to the Mac; its tooltip says how to stop.
//
// One seam: `new StreamKeys({ canvas, current, send, log })`
//   canvas            the scene's canvas (clicks on it keep the keyboard on the stream)
//   current()         the stream window in front (or null)
//   send(spec, ev)    an InputEvent ('I') for that stream: {action: 'key', usage, pressed, modifiers} or {action: 'text', text}
// and the app calls `take(spec)` when a click lands on a stream, `elsewhere(spec)` when a press lands on anything
// else, `forget(spec)` when a stream closes, `toggleSoft(spec)` for the bar's keyboard button, `typeText(spec, text)`
// for Paste from Here.
import { appURL } from './assets.js'

const HID = {}
'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach((c, i) => { HID['Key' + c] = 4 + i })
'1234567890'.split('').forEach((c, i) => { HID['Digit' + c] = 30 + i })
Object.assign(HID, {
  Enter: 40, NumpadEnter: 40, Escape: 41, Backspace: 42, Tab: 43, Space: 44, Minus: 45, Equal: 46, BracketLeft: 47,
  BracketRight: 48, Backslash: 49, Semicolon: 51, Quote: 52, Backquote: 53, Comma: 54, Period: 55, Slash: 56,
  CapsLock: 57, F1: 58, F2: 59, F3: 60, F4: 61, F5: 62, F6: 63, F7: 64, F8: 65, F9: 66, F10: 67, F11: 68, F12: 69,
  Home: 74, PageUp: 75, Delete: 76, End: 77, PageDown: 78, ArrowRight: 79, ArrowLeft: 80, ArrowDown: 81, ArrowUp: 82,
  IntlBackslash: 100, ControlLeft: 224, ShiftLeft: 225, AltLeft: 226, MetaLeft: 227, ControlRight: 228,
  ShiftRight: 229, AltRight: 230, MetaRight: 231,
})
/// By `event.key`, for a keydown that has no `code` (soft keyboards) or one the Mac has no key for.
const NAMED = { Enter: 40, Escape: 41, Backspace: 42, Tab: 43, ' ': 44, Delete: 76, Home: 74, End: 77, PageUp: 75, PageDown: 78,
  ArrowRight: 79, ArrowLeft: 80, ArrowDown: 81, ArrowUp: 82 }
/// A character → the key that types it on a US layout, for a latched Cmd / Ctrl from the strip (Cmd + "c" → Cmd-C).
const CHAR = { ' ': 44, '-': 45, '=': 46, '[': 47, ']': 48, '\\': 49, ';': 51, "'": 52, '`': 53, ',': 54, '.': 55, '/': 56 }
/// On a PC: Control ↔ Command, so Ctrl-C copies on the Mac.
const SWAP = { 224: 227, 228: 231, 227: 224, 231: 228 }
const SHIFT = 1, CONTROL = 2, OPTION = 4, COMMAND = 8, CAPS = 16
const SENTINEL = '​'   // the field is never truly empty, so a soft keyboard's Backspace always reaches us
const CHUNK = 20            // UTF-16 units per text event (what a Mac key event reliably carries)

const isMac = () => /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '')
const isTouch = () => !!globalThis.matchMedia?.('(hover: none) and (pointer: coarse)').matches
/// A press the person made: a trusted pointer or mouse event that ended a press (never a cancel, never a synthetic one).
const gesture = (e) => !!e && e.isTrusted !== false && /^(pointerup|mouseup|click|touchend)$/.test(e.type || '')
/// A field the person types into (the prompt bar, a sheet's search, a terminal) — not ours.
const editable = (el) => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ''))

export class StreamKeys {
  constructor({ canvas, current, send, log = () => {} }) {
    this.canvas = canvas; this.current = current; this.sendTo = send; this.log = log
    this.target = null           // the stream the keys go to
    this.held = new Map()        // code → usage sent down, so each gets its up (and all of them on release)
    this.sticky = 0              // Cmd / Ctrl latched on the strip
    this.shown = ''              // a soft keyboard's composition as far as the Mac has it
    this.swap = !isMac()
    this.build()
    addEventListener('keydown', (e) => this.onKeyDown(e), true)
    addEventListener('keyup', (e) => this.onKeyUp(e), true)
    addEventListener('blur', () => this.letGo())   // the window lost focus (Cmd-Tab away): no key stays down on the Mac
    canvas?.addEventListener('mousedown', (e) => { if (this.focused()) e.preventDefault() })   // a click on the stream keeps the field
    canvas?.addEventListener('touchend', (e) => { if (this.focused()) e.preventDefault() }, { passive: false })
    globalThis.visualViewport?.addEventListener('resize', () => this.placeStrip())
    globalThis.visualViewport?.addEventListener('scroll', () => this.placeStrip())
    // the person went into another field (the prompt bar most of all): the keys are its, not the stream's
    document.addEventListener?.('focusin', (e) => { if (this.target && e.target !== this.field && editable(e.target)) this.release() }, true)
  }

  // MARK: the seam

  /// A click landed on this stream: its keys go to the Mac now. On a desktop the hidden field takes the focus — only
  /// inside that tap (`tap`: the pointer event itself), never from a frame, a redraw or a promise; on a phone the soft
  /// keyboard stays as it is (the bar's keyboard button raises it). Without a tap (Paste from Here's clipboard
  /// answer) the keys are aimed but the focus stays where it is (Josh 10-09: "keyboard only on tap").
  take(spec, tap = null) {
    if (!spec) return
    if (this.target && this.target !== spec) this.letGo()
    this.target = spec
    if (gesture(tap) && (!isTouch() || this.focused())) this.focus()   // focus: gesture
    this.draw()
  }
  /// A press landed on something else (another window, a window's bar): stop, unless it was this stream's own chrome.
  elsewhere(spec) {
    if (!this.target || spec === this.target || spec?.stream === this.target) return
    this.release()
  }
  forget(spec) { if (this.target === spec) this.release() }
  /// Stop typing to the Mac.
  release() {
    this.letGo()
    const had = this.target
    this.target = null; this.sticky = 0
    if (this.focused()) this.field.blur()
    this.draw()
    if (had) this.log('keys: stopped typing to ' + (had.short || had.id))
  }
  /// The bar's keyboard button: raise or lower the soft keyboard (called inside the tap, so the browser allows it).
  toggleSoft(spec) {
    if (this.focused() && this.target === spec) { this.field.blur(); this.draw(); return }
    this.target = spec
    this.focus()   // focus: gesture (the bar's keyboard button, inside its tap)
    this.draw()
  }
  softUp(spec) { return isTouch() && this.focused() && this.target === spec }
  /// Text as typing (Paste from Here, a soft keyboard): lines as text, a Return between them.
  typeText(spec, text) {
    if (!spec || !text) return
    const lines = String(text).split(/\r\n|\r|\n/)
    lines.forEach((line, i) => {
      if (i) this.tap(40, 0, spec)
      let chunk = ''
      for (const ch of line) {   // by code point, so a surrogate pair is never split
        if (chunk.length + ch.length > CHUNK) { this.sendTo(spec, { action: 'text', text: chunk }); chunk = '' }
        chunk += ch
      }
      if (chunk) this.sendTo(spec, { action: 'text', text: chunk })
    })
  }

  // MARK: the keyboard

  focused() { return document.activeElement === this.field }
  /// Only ever from take(…, tap) and toggleSoft — both inside the person's tap.
  focus() { try { this.field.value = SENTINEL; this.field.focus({ preventScroll: true }); this.caret() } catch {} }   // focus: gesture
  caret() { try { this.field.setSelectionRange(this.field.value.length, this.field.value.length) } catch {} }
  /// Does this key event go to the Mac?
  listening(e) {
    if (!this.target) return false
    if (this.target !== this.current?.() || this.target.player?.canControl === false) { this.release(); return false }
    if (this.focused()) return true
    // no field focused (a phone tapped the stream, a hardware keyboard): keys still go, unless something else types
    return isTouch() && !editable(document.activeElement)
  }
  modifiers(e) {
    let m = (e.shiftKey ? SHIFT : 0) | (e.ctrlKey ? CONTROL : 0) | (e.altKey ? OPTION : 0) | (e.metaKey ? COMMAND : 0)
    if (this.swap) m = (m & ~(CONTROL | COMMAND)) | (e.ctrlKey ? COMMAND : 0) | (e.metaKey ? CONTROL : 0)
    if (e.getModifierState?.('CapsLock')) m |= CAPS
    return m | this.sticky
  }
  /// The browser's own: reload, tab switching, developer tools, and what it closes or opens whatever a page says.
  reserved(e) {
    const primary = isMac() ? e.metaKey : e.ctrlKey
    if (!isMac() && (e.code === 'F5' || e.code === 'F12')) return true
    if (!primary) return false
    if (e.code === 'Tab' || /^Digit[1-9]$/.test(e.code)) return true
    if (['KeyR', 'KeyW', 'KeyT', 'KeyN', 'KeyQ'].includes(e.code)) return true
    return e.code === 'KeyI' && (e.altKey || e.shiftKey)
  }

  onKeyDown(e) {
    if (!this.listening(e)) return
    if (e.key === 'Escape' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault(); e.stopImmediatePropagation(); this.release(); return
    }
    if (this.reserved(e)) return
    // composing (an IME, a dead key): the browser composes in the field; the text goes when it ends
    if (e.isComposing || e.keyCode === 229 || this.composing) return
    if (e.key === 'Dead') { this.dead = true; e.stopImmediatePropagation(); return }
    e.stopImmediatePropagation()   // not the arc's arrow keys, not the page's shortcuts
    const chord = e.ctrlKey || e.metaKey || e.altKey
    // the soft keyboard's characters come as beforeinput text (its keydowns have no reliable position)
    if (this.focused() && isTouch() && [...e.key].length === 1 && !chord && !this.sticky) return
    // after a dead key the browser did not compose (Windows): the accented character, as text
    if (this.dead && [...e.key].length === 1 && /[^\x00-\x7f]/.test(e.key)) { this.dead = false; e.preventDefault(); this.typeText(this.target, e.key); return }
    this.dead = false
    let usage = HID[e.code] ?? NAMED[e.key]
    if (usage == null) {
      // a key the Mac has no code for (the keypad's + or *): its character
      if ([...e.key].length === 1) { e.preventDefault(); this.typeChars(e.key); if (this.sticky) { this.sticky = 0; this.drawStrip() } }
      return
    }
    if (this.swap) usage = SWAP[usage] ?? usage
    e.preventDefault()
    const code = e.code || 'key:' + e.key
    this.held.set(code, usage)
    this.sendTo(this.target, { action: 'key', usage, pressed: true, modifiers: this.modifiers(e) })
    // a latched Cmd / Ctrl lasts one key (a soft keyboard's keys may never send their keyup)
    if (this.sticky && !(usage >= 224 && usage <= 231)) { this.onKeyUp(e, true); this.sticky = 0; this.drawStrip() }
  }

  onKeyUp(e, now = false) {
    const code = e.code || 'key:' + e.key
    const usage = this.held.get(code)
    if (usage == null || !this.target) return
    this.held.delete(code)
    if (!now) { e.preventDefault(); e.stopImmediatePropagation() }
    this.sendTo(this.target, { action: 'key', usage, pressed: false, modifiers: this.modifiers(e) })
  }

  /// Every key still down on the Mac comes up (focus left, typing stopped).
  letGo() {
    if (this.target) for (const usage of this.held.values()) this.sendTo(this.target, { action: 'key', usage, pressed: false, modifiers: 0 })
    this.held.clear(); this.dead = false; this.composing = false; this.shown = ''
  }

  /// A key tapped (down, up) with these modifiers.
  tap(usage, modifiers = 0, spec = this.target) {
    if (!spec) return
    this.sendTo(spec, { action: 'key', usage, pressed: true, modifiers })
    this.sendTo(spec, { action: 'key', usage, pressed: false, modifiers })
  }

  // MARK: the hidden field (soft keyboards, IMEs, dead keys, the browser's Edit › Paste)

  onBeforeInput(e) {
    if (!this.target) return
    const t = e.inputType
    if (t === 'insertCompositionText' || e.isComposing) return   // composing: its own events carry it
    e.preventDefault()
    const text = e.data ?? e.dataTransfer?.getData('text/plain') ?? ''
    const key = {
      insertLineBreak: [40, 0], insertParagraph: [40, 0], deleteContentBackward: [42, 0], deleteContentForward: [76, 0],
      deleteWordBackward: [42, OPTION], deleteWordForward: [76, OPTION], deleteSoftLineBackward: [42, COMMAND],
      deleteHardLineBackward: [42, COMMAND], deleteSoftLineForward: [14, CONTROL], deleteHardLineForward: [14, CONTROL],
      historyUndo: [29, COMMAND], historyRedo: [29, COMMAND | SHIFT],
    }[t]
    if (key) this.tap(key[0], key[1] | this.sticky)
    else if (/^insert/.test(t) && text) this.typeChars(text)
    if (this.sticky) { this.sticky = 0; this.drawStrip() }
    this.reset()
  }
  /// Soft-keyboard characters: as text, or — with Cmd / Ctrl latched — as that key (Cmd + "a" selects all).
  typeChars(text) {
    const chars = [...text]
    if (this.sticky && chars.length === 1) {
      const c = chars[0], lower = c.toLowerCase()
      const usage = /[a-z]/.test(lower) ? 4 + lower.charCodeAt(0) - 97 : /[1-9]/.test(c) ? 29 + Number(c) : c === '0' ? 39 : CHAR[c]
      if (usage != null) { this.tap(usage, this.sticky | (c !== lower ? SHIFT : 0)); return }
    }
    this.typeText(this.target, text)
  }
  /// A soft keyboard composes words (Android's keyboards, most of all): what it shows goes to the Mac as it changes —
  /// Backspaces for what changed, then the new end — so the Mac keeps up rather than waiting for the space.
  /// A desktop IME's candidates are not the words yet: that text goes once, when the composition ends.
  compose(now, done) {
    if (!this.target) return
    if (!isTouch() && !done) return
    const was = [...this.shown], is = [...now]
    let same = 0
    while (same < was.length && same < is.length && was[same] === is[same]) same++
    for (let i = same; i < was.length; i++) this.tap(42)
    const added = is.slice(same).join('')
    if (added) this.typeText(this.target, added)
    this.shown = done ? '' : now
  }
  reset() { if (this.field.value !== SENTINEL) this.field.value = SENTINEL; this.caret() }

  // MARK: drawing — the hidden field, "Typing to …", the strip of keys

  build() {
    if (!document.querySelector('link[data-streamkeys]')) {
      const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = appURL('core/streamkeys.css')
      link.dataset.streamkeys = ''; document.head.appendChild(link)
    }
    const field = this.field = document.createElement('textarea')
    field.id = 'stream-keys'
    field.setAttribute('aria-label', 'Keys to the Mac window')
    for (const [k, v] of Object.entries({ autocomplete: 'off', autocorrect: 'off', autocapitalize: 'off', spellcheck: 'false', enterkeyhint: 'enter', rows: '1' })) field.setAttribute(k, v)
    field.value = SENTINEL
    field.addEventListener('beforeinput', (e) => this.onBeforeInput(e))
    field.addEventListener('compositionstart', () => { this.composing = true; this.shown = '' })
    field.addEventListener('compositionend', (e) => {
      this.composing = false; this.dead = false
      this.compose(e.data || '', true)
      // some browsers commit into the field (with an input event) after this: that text has gone already
      this.committed = e.data || ''
      setTimeout(() => { this.committed = ''; this.reset() }, 0)
    })
    field.addEventListener('input', (e) => {
      if (this.composing || e.isComposing) { this.compose(field.value.replace(SENTINEL, ''), false); return }
      // anything that got in without a cancelable beforeinput: send it, then empty the field again
      const extra = field.value.replace(SENTINEL, '')
      if (extra && this.target && extra !== this.committed) this.typeText(this.target, extra)
      this.reset()
    })
    field.addEventListener('focus', () => this.draw())
    field.addEventListener('blur', () => setTimeout(() => {
      if (this.focused()) return
      if (!isTouch()) this.release()   // a desktop: the focus went somewhere else, so the keys do too
      else { this.letGo(); this.draw() }   // a phone: the soft keyboard went down; a tap on the stream still aims keys
    }, 200))
    document.body.appendChild(field)

    const pill = this.pill = document.createElement('button')
    pill.id = 'stream-typing'; pill.className = 'glass'; pill.hidden = true
    pill.title = 'Your keys go to this window on the Mac, as you press them — Backspace, arrows, Return, Tab, Esc and Cmd shortcuts too. ' +
      "Cmd-V pastes the Mac's clipboard; Paste from Here on the window's bar types this device's." +
      (isMac() ? '' : ' Ctrl is the Mac\'s Command here, the Windows key its Control.') +
      ' To stop: Shift-Esc, click here, or go to another window.'
    pill.addEventListener('mousedown', (e) => e.preventDefault())
    pill.addEventListener('click', () => this.release())
    document.body.appendChild(pill)

    const strip = this.strip = document.createElement('div')
    strip.id = 'stream-strip'; strip.className = 'glass'; strip.hidden = true
    const keys = [['esc', 'Esc', 41], ['tab', 'Tab', 43], ['cmd', '⌘', 'cmd'], ['ctrl', '⌃', 'ctrl'], ['left', '←', 80], ['up', '↑', 82], ['down', '↓', 81], ['right', '→', 79], ['hide', '⌨︎↓', 'hide']]
    for (const [id, label, what] of keys) {
      const b = document.createElement('button'); b.className = 'pill'; b.textContent = label; b.dataset.key = id
      b.title = { cmd: 'Command, for the next key', ctrl: 'Control, for the next key', hide: 'Lower the keyboard' }[id] || label
      // the field keeps the focus (and the soft keyboard stays up)
      b.addEventListener('pointerdown', (e) => e.preventDefault())
      b.addEventListener('mousedown', (e) => e.preventDefault())
      b.addEventListener('click', (e) => {
        e.preventDefault()
        if (what === 'hide') { this.field.blur(); return }
        if (what === 'cmd' || what === 'ctrl') { this.sticky ^= what === 'cmd' ? COMMAND : CONTROL; this.drawStrip(); return }
        this.tap(what, this.sticky); this.sticky = 0; this.drawStrip()
      })
      strip.appendChild(b)
    }
    document.body.appendChild(strip)
  }

  draw() {
    const on = !!this.target && this.focused()
    this.pill.hidden = !on
    if (on) this.pill.textContent = '⌨︎ Typing to ' + (this.target.name || this.target.short || 'the Mac window')
    this.strip.hidden = !(on && isTouch())
    this.drawStrip(); this.placeStrip()
    try { this.target?.chromeSpec?.dom.render() } catch {}   // the bar's keyboard button shows up or down
  }
  drawStrip() {
    for (const b of this.strip.querySelectorAll('[data-key="cmd"], [data-key="ctrl"]')) b.classList.toggle('on', !!(this.sticky & (b.dataset.key === 'cmd' ? COMMAND : CONTROL)))
  }
  /// The strip sits just above the soft keyboard (the visual viewport's bottom).
  placeStrip() {
    if (this.strip.hidden) return
    const vv = globalThis.visualViewport
    const lift = vv ? Math.max(0, innerHeight - (vv.height + vv.offsetTop)) : 0
    this.strip.style.bottom = (lift + 8) + 'px'
  }
}
